import { useState, useEffect } from 'react'
import './WeeklyNewsEditor.css'

const API_BASE_URL = window.location.origin + '/api'

interface NewsItem {
  id: string
  title: string
  content: string
  date?: string
}

interface WeeklyNewsData {
  title: string
  news: NewsItem[]
}

const generateId = () => Math.random().toString(36).substring(2, 15)

export default function WeeklyNewsEditor() {
  const [newsData, setNewsData] = useState<WeeklyNewsData>({
    title: 'Aktuální novinky týdne',
    news: []
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    loadNews()
  }, [])

  const loadNews = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/weekly-news`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      })

      if (response.ok) {
        const data = await response.json()
        if (data.news) {
          setNewsData(data.news)
        }
      }
    } catch (error) {
      console.error('Error loading weekly news:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const response = await fetch(`${API_BASE_URL}/weekly-news`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ news: newsData })
      })

      if (response.ok) {
        alert('Novinky byly úspěšně uloženy!')
      } else {
        const error = await response.json()
        alert('Chyba při ukládání: ' + (error.error || 'Neznámá chyba'))
      }
    } catch (error) {
      console.error('Error saving news:', error)
      alert('Chyba při ukládání novinek')
    } finally {
      setSaving(false)
    }
  }

  const addNewsItem = () => {
    const newItem: NewsItem = {
      id: generateId(),
      title: '',
      content: '',
      date: new Date().toLocaleDateString('cs-CZ')
    }
    setNewsData({
      ...newsData,
      news: [...newsData.news, newItem]
    })
  }

  const removeNewsItem = (id: string) => {
    if (!confirm('Opravdu chcete smazat tuto novinku?')) return
    setNewsData({
      ...newsData,
      news: newsData.news.filter(item => item.id !== id)
    })
  }

  const updateNewsItem = (id: string, field: string, value: string) => {
    setNewsData({
      ...newsData,
      news: newsData.news.map(item =>
        item.id === id ? { ...item, [field]: value } : item
      )
    })
  }

  const moveNewsItem = (id: string, direction: 'up' | 'down') => {
    const news = [...newsData.news]
    const index = news.findIndex(item => item.id === id)
    if (index === -1) return

    const newIndex = direction === 'up' ? index - 1 : index + 1
    if (newIndex < 0 || newIndex >= news.length) return

    ;[news[index], news[newIndex]] = [news[newIndex], news[index]]
    setNewsData({ ...newsData, news })
  }

  if (loading) {
    return <div className="weekly-news-editor-loading">Načítání...</div>
  }

  return (
    <div className="weekly-news-editor">
      <h2>📰 Úprava aktuálních novinek týdne</h2>
      <p style={{ color: '#666', marginBottom: '2rem' }}>
        Zde můžete upravit novinky, které se zobrazují na hlavní stránce intranetu.
      </p>

      <div className="editor-section">
        <label>Nadpis sekce:</label>
        <input
          type="text"
          value={newsData.title}
          onChange={(e) => setNewsData({ ...newsData, title: e.target.value })}
          className="editor-input"
          placeholder="Aktuální novinky týdne"
        />
      </div>

      <div className="news-items-container">
        {newsData.news.map((item, index) => (
          <div key={item.id} className="news-item-editor">
            <div className="news-item-header">
              <h4>Novinka #{index + 1}</h4>
              <div className="news-item-controls">
                <button
                  onClick={() => moveNewsItem(item.id, 'up')}
                  disabled={index === 0}
                  className="move-button"
                  title="Přesunout nahoru"
                >
                  ⬆️
                </button>
                <button
                  onClick={() => moveNewsItem(item.id, 'down')}
                  disabled={index === newsData.news.length - 1}
                  className="move-button"
                  title="Přesunout dolů"
                >
                  ⬇️
                </button>
                <button
                  onClick={() => removeNewsItem(item.id)}
                  className="delete-button"
                  title="Smazat"
                >
                  🗑️
                </button>
              </div>
            </div>

            <label>Nadpis novinky:</label>
            <input
              type="text"
              value={item.title}
              onChange={(e) => updateNewsItem(item.id, 'title', e.target.value)}
              className="editor-input"
              placeholder="Nadpis novinky"
            />

            <label>Datum (volitelné):</label>
            <input
              type="text"
              value={item.date || ''}
              onChange={(e) => updateNewsItem(item.id, 'date', e.target.value)}
              className="editor-input"
              placeholder="např. 6. prosince 2024"
            />

            <label>Obsah novinky:</label>
            <textarea
              value={item.content}
              onChange={(e) => updateNewsItem(item.id, 'content', e.target.value)}
              className="editor-textarea"
              rows={4}
              placeholder="Text novinky"
            />
          </div>
        ))}
      </div>

      <button onClick={addNewsItem} className="add-news-button">
        ➕ Přidat novinku
      </button>

      <div className="editor-actions">
        <button
          onClick={handleSave}
          disabled={saving}
          className="save-button"
        >
          {saving ? 'Ukládání...' : '💾 Uložit změny'}
        </button>
      </div>
    </div>
  )
}



